import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { getFirebaseInstance, tenantExists } from "../../utils/firebase/config";
import { doc, getDoc, getFirestore } from "firebase/firestore";

const FirebaseContext = createContext();

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
};

export const FirebaseProvider = ({ tenant, children, onAuthChange }) => {
  const [firebaseInstance, setFirebaseInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);
  const [loadingAuthUser, setLoadingAuthUser] = useState(false);

  useEffect(() => {
    const initializeFirebase = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if tenant exists
        if (!tenantExists(tenant)) {
          console.warn(
            `Tenant '${tenant}' not found. Using default configuration.`,
          );
        }

        // Get Firebase instance for tenant
        const instance = getFirebaseInstance(tenant);
        setFirebaseInstance(instance);

        // Notify parent component about auth change
        if (onAuthChange) {
          onAuthChange(instance.auth);
        }

        console.log(`Firebase Context initialized for tenant: ${tenant}`);
      } catch (err) {
        console.error("Error initializing Firebase Context:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (tenant) {
      initializeFirebase();
    }
  }, [tenant, onAuthChange]);

  // Effect to listen for auth state changes and fetch user data
  useEffect(() => {
    const auth = firebaseInstance?.auth;

    if (!auth) {
      setCurrentUserData(null);
      setIsAuthorizedUser(false);
      return;
    }

    // Get the default Firestore database instance
    const defaultFirebaseAppInstance = getFirebaseInstance("default");
    const defaultDb = getFirestore(defaultFirebaseAppInstance.app);

    const fetchUserData = async (user) => {
      if (user && user.email) {
        setLoadingAuthUser(true);
        try {
          const userDocRef = doc(defaultDb, "users", user.email);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            setCurrentUserData(data);
            setIsAuthorizedUser(data.isActive === true); // Only isActive check for general access
          } else {
            setCurrentUserData(null);
            setIsAuthorizedUser(false);
          }
        } catch (err) {
          console.error("Error fetching user data from default DB:", err);
          setCurrentUserData(null);
          setIsAuthorizedUser(false);
        } finally {
          setLoadingAuthUser(false);
        }
      } else {
        setCurrentUserData(null);
        setIsAuthorizedUser(false);
      }
    };

    // Listen to auth state changes directly here
    const unsubscribe = auth.onAuthStateChanged(fetchUserData);
    return () => unsubscribe();
  }, [firebaseInstance]); // Still depends on firebaseInstance for auth object

  const reload = useCallback(() => {
    if (tenant) {
      const instance = getFirebaseInstance(tenant);
      setFirebaseInstance(instance);
    }
  }, [tenant]);

  const value = useMemo(
    () => ({
      // Firebase services
      auth: firebaseInstance?.auth,
      storage: firebaseInstance?.storage,
      db: firebaseInstance?.db,
      app: firebaseInstance?.app,

      // Tenant information
      tenant,
      config: firebaseInstance?.config,
      databaseId: firebaseInstance?.databaseId,
      folderPrefix: firebaseInstance?.folderPrefix,

      // States
      loading,
      error,
      currentUserData,
      isAuthorizedUser,
      loadingAuthUser,

      // Utility functions
      isReady: !loading && !error && Boolean(firebaseInstance),

      // Reload configuration
      reload,
    }),
    [
      firebaseInstance,
      tenant,
      loading,
      error,
      currentUserData,
      isAuthorizedUser,
      loadingAuthUser,
      reload,
    ],
  );

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext;
